import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, Image as ImageIcon, Heart, Moon, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useTheme } from "@/contexts/ThemeContext";
import { processImageUrlSync } from "@/utils/imageProxy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Favorites = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nsfwMode, setNsfwMode] = useState(false);

  useEffect(() => {
    const savedNsfw = localStorage.getItem("nsfwMode") === "true";
    setNsfwMode(savedNsfw);
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/favorites`);
      setArticles(response.data);
    } catch (error) {
      console.error("Error fetching favorites:", error);
      toast.error("Failed to fetch favorites");
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async (articleId, e) => {
    e.stopPropagation();
    try {
      const response = await axios.post(`${API}/favorites/toggle/${articleId}`);
      toast.success(response.data.message);
      fetchFavorites();
    } catch (error) {
      console.error("Error toggling favorite:", error);
      toast.error("Failed to update favorite");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate("/")}
                className="hover:bg-gray-100 dark:hover:bg-gray-800"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2" data-testid="favorites-title">
                <Heart className="w-8 h-8 fill-red-500 text-red-500" />
                My Favorites
              </h1>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle"
            >
              {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Articles Grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center min-h-[400px]" data-testid="loading-indicator">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-20" data-testid="no-favorites">
            <Heart className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h2 className="text-2xl font-semibold text-gray-700 dark:text-gray-300 mb-2">No favorites yet</h2>
            <p className="text-gray-500">Start adding articles to your favorites</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="favorites-grid">
            {articles.map((article, index) => (
              <div
                key={article.id}
                className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-md hover:shadow-2xl transform hover:-translate-y-1 border border-gray-200 dark:border-gray-700 relative group"
                style={{ transition: "all 0.3s ease" }}
                data-testid={`article-card-${index}`}
              >
                <div onClick={() => navigate(`/article/${article.id}`)} className="cursor-pointer">
                  <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 relative overflow-hidden flex items-center justify-center">
                    {article.thumbnail ? (
                      <img
                        src={processImageUrlSync(article.thumbnail, article.source)}
                        alt={article.title}
                        className={`w-full h-full object-contain group-hover:scale-105 ${nsfwMode ? 'nsfw-blur' : ''}`}
                        style={{ transition: "transform 0.5s ease" }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="w-12 h-12 text-gray-400" />
                      </div>
                    )}
                    <div className="absolute top-2 right-2">
                      <span className="px-3 py-1 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-full text-xs font-semibold text-gray-700 dark:text-gray-300">
                        {article.source}
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400" style={{ transition: "color 0.3s ease" }}>
                      {article.title}
                    </h3>
                    {article.tags && article.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {article.tags.slice(0, 3).map((tag, idx) => (
                          <span key={idx} className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Favorite Button */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 left-2 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-900"
                  onClick={(e) => toggleFavorite(article.id, e)}
                  data-testid={`favorite-button-${index}`}
                >
                  <Heart className="w-5 h-5 fill-red-500 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Favorites;